/**
 * PublicProfilePage - Read-Only Profile View
 *
 * Shows another user's profile in read-only mode with messaging action.
 * Based on ProfilePage layout but without edit capabilities.
 *
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7D
 * Issue #920: Added block/report user functionality
 */

import { useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { usePublicProfile } from '../hooks/useProfile';
import { useAuth } from '../hooks/useAuth';
import { useOroMutation, OroOperation } from '../hooks/useOroMutation';
import { TopBar, Avatar, UserBadge, Button, EmptyState, ProfileHeaderSkeleton, ActivityCardSkeleton, Skeleton } from '../components';
import { blocksApi, type BlockResponse } from '../lib/api/blocks';
import { invalidateBlockSensitiveQueries } from '../lib/blockSensitiveQueries';
import { reportsApi, type CreateReportPayload, type CreateReportResponse } from '../lib/api/reports';
import { X, AlertTriangle, UserX, MapPin, MoreVertical, Users } from 'lucide-react';
import { getZIndexValue } from '../config/zIndex';

type ReportReason = CreateReportPayload['reason'];

// Club context passed via navigation state (#1232, #1233, #1235)
interface ClubContext {
  id: string;
  slug: string;
  name: string;
  hero_image_url?: string | null;
  avatar_image_url?: string | null;
}

export default function PublicProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Get club context from navigation state (when coming from club members page)
  const clubContext = (location.state as { club?: ClubContext } | null)?.club;

  // Block/Report modal state (#920); #2254 wires the overflow-menu trigger —
  // the modals existed but nothing ever opened them.
  const [showActions, setShowActions] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportReason, setReportReason] = useState<ReportReason>('inappropriate_content');
  const [reportNotes, setReportNotes] = useState('');

  // Fetch public profile using React Query
  const { data: profile, isLoading, error: fetchError } = usePublicProfile(id || '');

  // Block/report only make sense against SOMEONE ELSE while signed in (#2254)
  const canModerate = !!user && !!id && user.id !== id;

  // Block mutation
  const blockMutation = useOroMutation<BlockResponse, string>({
    mutationFn: (userId) => blocksApi.blockUser(userId),
    oro: { op: OroOperation.BlockUser },
    successToast: 'User blocked',
    onSuccess: (_data, blockedUserId) => {
      queryClient.invalidateQueries({ queryKey: ['blocked-users'] });
      // #2297: REMOVE (not invalidate) the cached profile — staleTime is 5min,
      // so re-entering /u/:id after blocking would otherwise render the full
      // cached profile without a refetch. Remove means the next visit hits the
      // server, which now 404s (blocked-viewer semantics), with no stale
      // placeholder render in between.
      queryClient.removeQueries({ queryKey: ['publicProfile', blockedUserId] });
      // #3258: the server now filters the blocked user out of chat + rosters,
      // but already-fetched queries stay fresh for their staleTime — navigating
      // back to a room or member list would keep rendering their content.
      // One shared helper invalidates EVERY block-filtered prefix (PR #3266
      // r2) so this list cannot drift from the unblock path.
      invalidateBlockSensitiveQueries(queryClient);
      setShowBlockConfirm(false);
      navigate(-1); // Go back after blocking
    },
  });

  // Report mutation
  const reportMutation = useOroMutation<CreateReportResponse, CreateReportPayload>({
    mutationFn: (payload) => reportsApi.createReport(payload),
    oro: { op: OroOperation.ReportUser },
    successToast: 'Report submitted. Our team will review it.',
    onSuccess: () => {
      setShowReportForm(false);
      setReportReason('inappropriate_content');
      setReportNotes('');
    },
  });

  const handleBlock = () => {
    if (!id) return;
    blockMutation.mutate(id);
  };

  const handleReport = () => {
    if (!id) return;
    reportMutation.mutate({
      reported_id: id,
      context_type: 'user',
      reason: reportReason,
      notes: reportNotes || undefined,
    });
  };

  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Profile" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-6 space-y-6">
            <ProfileHeaderSkeleton />
            
            {/* Activity section skeleton */}
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              {[1, 2, 3].map((i) => (
                <ActivityCardSkeleton key={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (fetchError || !profile || !id) {
    // ADR-UX-005: Human, calm, specific error messages
    const errorMessage = fetchError
      ? "We couldn't find this profile"
      : !id
      ? "We couldn't load this profile"
      : "This profile isn't available";
      
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Profile" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12">
            <EmptyState
              icon={<UserX className="w-full h-full" />}
              title="Profile not found"
              message={errorMessage}
              actionLabel="Go back"
              onAction={() => navigate(-1)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar
        title={profile.public_display_name || 'Profile'}
        onBack={() => navigate(-1)}
      />
      
      <div className="flex-1 overflow-y-auto pt-14">
        {/* Hero Section */}
        <div className="relative">
          {/* Cover Photo - Priority: user cover → club hero → topo pattern (#1234) */}
          <div className="h-40 bg-linear-to-br from-accent/20 to-accent/5 relative overflow-hidden">
            {profile.cover_image_url ? (
              <img
                src={profile.cover_image_url}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : clubContext?.hero_image_url ? (
              <img
                src={clubContext.hero_image_url}
                alt=""
                className="absolute inset-0 w-full h-full object-cover"
              />
            ) : (
              /* Topo Pattern Overlay (fallback) */
              <svg className="absolute inset-0 w-full h-full opacity-10" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice">
                <defs>
                  <pattern id="topo-public" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                    <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="0.5"/>
                    <circle cx="10" cy="10" r="4" fill="none" stroke="currentColor" strokeWidth="0.3"/>
                  </pattern>
                </defs>
                <rect width="100" height="100" fill="url(#topo-public)"/>
              </svg>
            )}
          </div>

          {/* Avatar Card - Overlapping cover */}
          <div className="px-4 -mt-12 relative z-10">
            <div className="bg-oro-surface border border-oro-border rounded-2xl p-4 shadow-card">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <Avatar
                    name={profile.public_display_name}
                    profile_image_url={profile.profile_image_url}
                    size="lg"
                    className="ring-2 ring-oro-bg"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h1 className="text-xl font-bold text-text-primary truncate">
                      {profile.public_display_name}
                    </h1>
                    <UserBadge size="sm" />
                  </div>
                  {profile.tagline && (
                    <p className="text-sm text-text-secondary mb-2 line-clamp-2">
                      {profile.tagline}
                    </p>
                  )}
                  {profile.location && (
                    <p className="text-xs text-text-muted flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {profile.location}
                    </p>
                  )}
                  {/* Club context signature (#1233) - quiet, supportive, Layer 2 context */}
                  {clubContext?.name && (
                    <button
                      onClick={() => navigate(`/clubs/${clubContext.slug}`)}
                      className="mt-2 flex items-center gap-1.5 text-xs text-text-muted hover:text-text-secondary transition-colors"
                    >
                      <Users className="w-3 h-3 text-accent/60" />
                      <span>{clubContext.name}</span>
                    </button>
                  )}
                </div>

                {/* Overflow menu — block/report entry point (#920/#2254) */}
                {canModerate && (
                  <button
                    onClick={() => setShowActions(true)}
                    className="w-9 h-9 self-start rounded-lg bg-oro-surface-alt hover:bg-oro-surface border border-oro-border flex items-center justify-center transition-colors"
                    aria-label="Profile actions"
                  >
                    <MoreVertical className="w-4 h-4 text-text-primary" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 py-6 space-y-6">
          {/* Stats Row (#1510) */}
          <div className="bg-oro-surface border border-oro-border rounded-2xl p-4">
            <div className="flex items-center justify-around">
              <div className="text-center">
                <div className="text-xl font-bold text-text-primary">{profile.event_count ?? 0}</div>
                <div className="text-xs text-text-muted">Events</div>
              </div>
              <div className="w-px h-8 bg-oro-border"></div>
              <div className="text-center">
                <div className="text-xl font-bold text-text-primary">{profile.club_count ?? 0}</div>
                <div className="text-xs text-text-muted">Clubs</div>
              </div>
            </div>
          </div>

          {/* Shared Clubs — the only real content on a public profile today.
              The Activity ("Private activity") and Stats ("coming soon") tabs
              were placeholders and were removed for App Store compliance
              (#3277); with one section left we render it without a tab strip
              (a one-tab strip is itself a placeholder). */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-text-primary">Shared Clubs</h3>
            {clubContext ? (
                /* Show shared club from navigation context (#1235) */
                <button
                  onClick={() => navigate(`/clubs/${clubContext.slug}`)}
                  className="w-full bg-oro-surface border border-oro-border rounded-xl p-4 flex items-center gap-4 hover:border-accent/30 transition-colors text-left"
                >
                  <div className="w-12 h-12 rounded-full bg-oro-surface-alt flex items-center justify-center overflow-hidden shrink-0">
                    {clubContext.avatar_image_url ? (
                      <img
                        src={clubContext.avatar_image_url}
                        alt={clubContext.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Users className="w-6 h-6 text-text-muted" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-text-primary truncate">{clubContext.name}</p>
                    <p className="text-sm text-text-muted">You're both members</p>
                  </div>
                </button>
              ) : (
                <EmptyState
                  icon={<Users className="w-full h-full" />}
                  title="No shared clubs"
                  message="View this profile from a club to see shared memberships"
                />
              )}
          </div>
        </div>
      </div>

      {/* Profile Actions Sheet (#2254) — house pattern from ClubMembersPage #1227 */}
      {showActions && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            style={{ zIndex: getZIndexValue('overlay') }}
            onClick={() => setShowActions(false)}
            aria-hidden="true"
          />

          {/* Bottom Sheet */}
          <div
            className="fixed inset-x-0 bottom-0 bg-oro-surface border-t border-oro-border rounded-t-2xl shadow-floating animate-in slide-in-from-bottom duration-200"
            style={{ zIndex: getZIndexValue('modal') }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="profile-actions-title"
          >
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-oro-border rounded-full" />
            </div>

            <div className="px-4 pb-8 pt-2">
              <h3 id="profile-actions-title" className="font-semibold text-text-primary mb-4">
                {profile.public_display_name}
              </h3>

              <div className="space-y-1">
                <button
                  onClick={() => {
                    setShowActions(false);
                    setShowReportForm(true);
                  }}
                  className="flex items-center gap-3 w-full p-4 rounded-lg text-text-primary hover:bg-oro-surface-alt transition-colors"
                >
                  <AlertTriangle className="w-5 h-5 text-accent" />
                  <span className="font-medium">Report user</span>
                </button>

                <button
                  onClick={() => {
                    setShowActions(false);
                    setShowBlockConfirm(true);
                  }}
                  className="flex items-center gap-3 w-full p-4 rounded-lg text-oro-error hover:bg-oro-error/10 transition-colors"
                >
                  <UserX className="w-5 h-5" />
                  <span className="font-medium">Block user</span>
                </button>

                <button
                  onClick={() => setShowActions(false)}
                  className="flex items-center justify-center w-full p-4 rounded-lg text-text-secondary hover:bg-oro-surface-alt transition-colors"
                >
                  <span>Cancel</span>
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Block Confirmation Modal (#920) */}
      {showBlockConfirm && (
        <>
          <div
            className="fixed inset-0 bg-black/50"
            style={{ zIndex: 50 }}
            onClick={() => setShowBlockConfirm(false)}
            aria-hidden="true"
          />
          <div
            className="fixed inset-x-4 top-1/3 max-w-md mx-auto bg-oro-surface border border-oro-border rounded-xl shadow-floating p-6"
            style={{ zIndex: 60 }}
            role="dialog"
            aria-modal="true"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-oro-error/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-oro-error" />
              </div>
              <div>
                <h3 className="font-semibold text-text-primary">Block {profile?.public_display_name}?</h3>
                <p className="text-sm text-text-secondary">They won't be able to see your profile or message you.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="secondary"
                size="md"
                onClick={() => setShowBlockConfirm(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="md"
                onClick={handleBlock}
                disabled={blockMutation.isPending}
                className="flex-1"
              >
                {blockMutation.isPending ? 'Blocking...' : 'Block'}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Report Form Modal (#920) */}
      {showReportForm && (
        <>
          <div
            className="fixed inset-0 bg-black/50"
            style={{ zIndex: 50 }}
            onClick={() => setShowReportForm(false)}
            aria-hidden="true"
          />
          <div
            className="fixed inset-x-4 top-20 bottom-20 max-w-md mx-auto bg-oro-surface border border-oro-border rounded-xl shadow-floating overflow-y-auto"
            style={{ zIndex: 60 }}
            role="dialog"
            aria-modal="true"
          >
            <div className="sticky top-0 bg-oro-surface border-b border-oro-border px-4 py-3 flex items-center justify-between">
              <h3 className="font-semibold text-text-primary">Report {profile?.public_display_name}</h3>
              <button
                onClick={() => setShowReportForm(false)}
                className="p-2 -mr-2 text-text-secondary hover:text-text-primary transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Reason Selection */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-text-primary">Reason</label>
                <div className="space-y-2">
                  {[
                    { value: 'spam' as const, label: 'Spam or advertising' },
                    { value: 'harassment' as const, label: 'Harassment or bullying' },
                    { value: 'hate_speech' as const, label: 'Hate speech' },
                    { value: 'safety_concern' as const, label: 'Safety concern' },
                    { value: 'inappropriate_content' as const, label: 'Inappropriate content' },
                    { value: 'other' as const, label: 'Other concern' },
                  ].map((reason) => (
                    <label
                      key={reason.value}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        reportReason === reason.value
                          ? 'border-accent bg-accent/5'
                          : 'border-oro-border hover:border-oro-border-hover'
                      }`}
                    >
                      <input
                        type="radio"
                        name="reportReason"
                        value={reason.value}
                        checked={reportReason === reason.value}
                        onChange={(e) => setReportReason(e.target.value as ReportReason)}
                        className="sr-only"
                      />
                      <div
                        className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          reportReason === reason.value ? 'border-accent' : 'border-oro-border'
                        }`}
                      >
                        {reportReason === reason.value && (
                          <div className="w-2 h-2 rounded-full bg-accent" />
                        )}
                      </div>
                      <span className="text-text-primary">{reason.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="text-sm font-medium text-text-primary block mb-2">
                  Additional details (optional)
                </label>
                <textarea
                  value={reportNotes}
                  onChange={(e) => setReportNotes(e.target.value)}
                  placeholder="Tell us more about the issue..."
                  className="w-full bg-oro-surface-alt border border-oro-border rounded-lg p-3 text-text-primary placeholder:text-text-muted focus:border-accent focus:outline-none resize-none"
                  rows={3}
                />
              </div>

              <Button
                variant="primary"
                size="md"
                onClick={handleReport}
                disabled={reportMutation.isPending}
                className="w-full"
              >
                {reportMutation.isPending ? 'Submitting...' : 'Submit Report'}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}